import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Paper, Fab } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import moment from 'moment';
import styled from 'styled-components';
import { AlertBar, Select, SeriesControls } from '~/shared/components';
import ReactLoading from 'react-loading';
import { tooltipCfg } from '~/shared/config/tooltipCfg';
import { colors, plotBandOpacity } from '~/shared/constants';
import { useContainerReady, useDebouncedResizeObserver } from '~/shared/hooks';
import { downloadHeatmapData, fetchTopicExplainability } from '~/shared/services/api';
import { useNavAndFilters, useHeatmapData, useScroller } from '~/shared/services/charts';
import { simpleMovingAverage } from '~/shared/utils';
import {
  DEFAULT_RANGE_SELECTOR_INDEX,
  getRangeSelectorIndexFromWindow,
  getRangeSelectorWindowByIndex,
} from '~/shared/utils/timeWindow';
import { deriveAxisLimits } from '~/shared/utils/chartAxis';
import { hex2rgba } from '../../utils/colors/hex2rgba';
import FilterBar from './FilterBar';
import IndicatorLineChart from './Indicator/IndicatorLineChart';
import IndicatorStackedAreaChart from './Indicator/IndicatorStackedAreaChart';
import IndicatorTable from './Indicator/IndicatorTable';
import YAxisLineChart from './YAxisLineChart/YAxisLineChart';
import type {
  TopicExplainabilityPhase,
  TopicExplainabilityStateChangeHandler,
  TopicExplainabilityExternalState,
} from './topicExplainability.types';
import {
  normalizeExplainabilityTitle,
  resolveExplainabilityDestinationTitle,
  transformExplainabilityResponse,
} from './topicExplainability.utils';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const WINDOW_DURATION_MS: Record<string, number> = {
  '1M': 31 * MS_PER_DAY,
  '3M': 92 * MS_PER_DAY,
  '6M': 183 * MS_PER_DAY,
  '1Y': 365 * MS_PER_DAY,
};

const computeWindowRange = (windowName: string | null, xValues: number[]) => {
  if (!windowName || !xValues.length) {
    return null;
  }

  const dataMin = Math.min(...xValues);
  const dataMax = Math.max(...xValues);
  if (!Number.isFinite(dataMin) || !Number.isFinite(dataMax)) {
    return null;
  }

  if (windowName === 'MAX') {
    return { min: dataMin, max: dataMax };
  }

  if (windowName === 'YTD') {
    const endDate = new Date(dataMax);
    const startOfYear = Date.UTC(endDate.getUTCFullYear(), 0, 1);
    const start = Math.max(dataMin, startOfYear);
    return { min: start, max: dataMax };
  }

  const duration = WINDOW_DURATION_MS[windowName];
  if (!duration) {
    return null;
  }

  let start = dataMax - duration;
  if (!Number.isFinite(start)) {
    return null;
  }
  start = Math.max(dataMin, start);
  if (!Number.isFinite(start) || start >= dataMax) {
    start = dataMin;
  }
  return { min: start, max: dataMax };
};

const DEFAULT_SMA_OPTIONS = [1, 7, 30, 90, 180, 365];
const DEFAULT_SMA_VALUE = 30;

const ScrollWrap = styled.div`
  flex: 1;
  overflow-y: scroll;
  height: 100%;
  max-width: 100%;
  ${({ theme }: any) => `
    padding: ${theme.spacing(3)}px;
  `};
`;

const IndicatorsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const IndicatorContainer = styled.div<{ $iswide: number }>`
  min-width: calc(50% - 5px);
  ${({ $iswide }) => $iswide === 1 && 'flex: 1;'}
`;

const MainContainer = styled(Paper)`
  max-width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 460px;
`;

const ContentContainer = styled.div`
  display: flex;
  padding: 4px;
`;

const HeatmapContainer = styled.div`
  flex: 1;
  position: relative;
  padding: 8px;
`;

const FabContainer = styled.div`
  position: relative;
  button {
    position: absolute;
    min-height: 26px;
    height: 26px;
    width: 26px;
    right: 2px;
    bottom: 2px;
    background: var(--background-chart-fab);
    color: var(--color-chart-fab);
  }
`;

const RelativeContainer = styled.div`
  position: relative;
  height: 100%;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background-chart, #ffffff);
  z-index: 2;
`;

const OverlayLoader = styled(ReactLoading).attrs({
  color: 'var(--loader-color)',
  type: 'spin',
})`
  width: 100px;
  height: 100px;
`;

const ContentFade = styled.div<{ $visible: number }>`
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.2s ease;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
`;

const SelectTitle = styled.div`
  display: flex;
  align-items: center;
  font-weight: 400;
  font-size: 13px;
  height: 24px;
`;

const ControlsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 32px;
  right: 65px;
  gap: 6px;
  align-items: flex-end;

  .select-title {
    padding-right: 3px !important;
    padding-top: 2px !important;
  }

  .sma-row .MuiOutlinedInput-root {
    height: 34px !important;
    min-height: 34px !important;
    padding: 0 8px !important;
    display: flex !important;
    align-items: center !important;
    background: var(--background-chart);
  }

  .sma-row .MuiOutlinedInput-input {
    padding-top: 6px !important;
    padding-bottom: 6px !important;
    display: flex !important;
    align-items: center !important;
  }

  .sma-row .MuiSelect-icon {
    top: 3px !important;
  }

  .sma-row fieldset {
    top: -4px !important;
    height: 37px !important;
  }
`;

function useHeatmapClick(navHirarchyItem: any, onScroll: any, filterValues: any) {
  const [indicatorSections, setIndicatorSections] = useState<any[]>([]);
  useEffect(() => {
    setIndicatorSections([]);
  }, [navHirarchyItem, filterValues]);
  const onClick = useCallback(
    (cellInfo: { source: string; topic?: string; symbol?: string }) => {
      setIndicatorSections([]);
      setTimeout(
        () =>
          navHirarchyItem &&
          filterValues &&
          setIndicatorSections(
            navHirarchyItem?.indicators[cellInfo?.source]?.map((indicatorObj: any) => {
              const indicator = indicatorObj && indicatorObj[0];
              const type = indicatorObj && indicatorObj[1];
              return { indicator, cellInfo, type };
            }),
          ),
        10,
      );
      setTimeout(onScroll, 200);
    },
    [navHirarchyItem, filterValues, onScroll],
  );
  return { indicatorSections, onClick };
}

interface ISeriesObj {
  name: string;
  data: number[][];
  showInNavigator: boolean;
  color: string | undefined;
  custom?: {
    description?: string;
  };
}

export default function YAxisLineChartPage({
  navHirarchy,
  gridIndex,
  gridHeight,
  onGridClick,
  gridFilterValues,
  gridFilterValuesID,
  gridPrism,
  onTopicExplainabilityStateChange,
}: any) {
  const { scrollerRef, scrollToTop, scrollToBottom } = useScroller();
  const { category, subcategory, subsubcategory, prism, navHirarchyItem, filterValues, filterValuesID, prismId }: any =
    useNavAndFilters(navHirarchy, gridIndex);

  const normalizedGridIndex = typeof gridIndex === 'string' ? Number(gridIndex) : gridIndex;
  const isPrimaryNarrativeChart =
    normalizedGridIndex === undefined || normalizedGridIndex === null || normalizedGridIndex === 0;

  const allowControlsArray = [
    'CentralBanksMembersLinesFED',
    'CentralBanksMembersLinesECB',
    'CentralBanksMembersLinesBOE',
    'CentralBanksGroupsLinesFED',
    'CentralBanksGroupsLinesECB',
    'CentralBanksGroupsLinesBOE',
    'CentralBanksGroupsDisagreementFED',
    'CentralBanksGroupsDisagreementECB',
    'CentralBanksGroupsDisagreementBOE',
  ];
  const showControls = allowControlsArray.includes(navHirarchyItem.name);

  const {
    data: allData,
    error,
    isLoading,
    chartTitle,
    chartSubtitle,
    chartYtitle,
    smaEnabled,
    smaConfig,
    meanEnabled,
    dropdownLegendEnabled,
    defaultTimeWindow,
    yAxisRangeMode,
    yAxisPaddingRatio,
  } = useHeatmapData(category, subcategory, subsubcategory, prism, gridFilterValuesID || filterValuesID, prismId);

  const resolvedChartTitle = useMemo(() => {
    if (chartTitle && chartTitle.trim().length > 0) {
      return chartTitle.trim();
    }
    const fallback = navHirarchyItem?.heatmap?.title ?? navHirarchyItem?.heatmap?.name ?? navHirarchyItem?.name ?? '';
    return (fallback || '').trim();
  }, [chartTitle, navHirarchyItem]);

  const allowSma = showControls || smaEnabled;
  const allowMean = showControls || meanEnabled;
  const allowDropdownLegend = showControls || dropdownLegendEnabled;

  const [data, setData] = useState<any>(allData);

  const topicExplainabilityRequestId = React.useRef(0);

  const smaOptions = React.useMemo<number[]>(() => {
    if (smaConfig?.values?.length) {
      return smaConfig.values;
    }
    return DEFAULT_SMA_OPTIONS;
  }, [smaConfig]);

  const resolvedDefaultSma = React.useMemo<number>(() => {
    if (smaConfig?.defaultValue !== undefined) {
      return smaConfig.defaultValue;
    }
    if (smaOptions.includes(DEFAULT_SMA_VALUE)) {
      return DEFAULT_SMA_VALUE;
    }
    return smaOptions[0] ?? DEFAULT_SMA_VALUE;
  }, [smaConfig, smaOptions]);

  const [smaValue, setSmaValue] = React.useState<number>(() => (allowSma ? resolvedDefaultSma : 1));
  const [chartTooltipVisible, setChartTooltipVisible] = React.useState(false);

  const handleSMAChange = useCallback(
    (val: any) => {
      if (!allowSma) {
        return;
      }
      setSmaValue(Number(val));
    },
    [allowSma],
  );

  const narrativeTopic = useMemo(() => {
    const source = gridFilterValues || filterValues || {};
    const rawTopic =
      source?.Narrative ??
      source?.narrative ??
      source?.['Narrative'] ??
      source?.['narrative'] ??
      filterValuesID?.Narrative ??
      filterValuesID?.narrative ??
      '';

    if (typeof rawTopic === 'string') return rawTopic;
    if (rawTopic && typeof rawTopic === 'object') {
      if ('name' in rawTopic && rawTopic.name) return String(rawTopic.name);
      if ('value' in rawTopic && rawTopic.value) return String(rawTopic.value);
      if ('id' in rawTopic && rawTopic.id) return String(rawTopic.id);
    }
    return rawTopic ? String(rawTopic) : '';
  }, [gridFilterValues, filterValues, filterValuesID]);

  const normalizedNarrativeTopic = useMemo(() => narrativeTopic.trim(), [narrativeTopic]);

  const chartExplainabilitySourceTitle = useMemo(() => resolvedChartTitle, [resolvedChartTitle]);

  const explicitDestinationTitle = useMemo(() => {
    const heatmap = navHirarchyItem?.heatmap ?? {};
    return heatmap?.childTable ?? heatmap?.child_table ?? heatmap?.['child-table'] ?? heatmap?.childtable ?? null;
  }, [navHirarchyItem]);

  const resolvedExplainabilityDestinationTitle = useMemo(
    () => resolveExplainabilityDestinationTitle(chartExplainabilitySourceTitle, explicitDestinationTitle),
    [chartExplainabilitySourceTitle, explicitDestinationTitle],
  );

  const hasExplainabilityDestination = useMemo(
    () => Boolean(normalizeExplainabilityTitle(resolvedExplainabilityDestinationTitle)),
    [resolvedExplainabilityDestinationTitle],
  );

  const emitTopicExplainabilityChange = useCallback(
    (phase: TopicExplainabilityPhase, overrides?: Partial<TopicExplainabilityExternalState>) => {
      const handler = onTopicExplainabilityStateChange as TopicExplainabilityStateChangeHandler | undefined;
      if (!handler) return;
      handler({
        topic: overrides?.topic ?? normalizedNarrativeTopic,
        phase,
        loading: overrides?.loading ?? phase === 'loading',
        error: overrides?.error ?? null,
        data: overrides?.data ?? null,
        destinationTitle: overrides?.destinationTitle ?? null,
        sourceTitle: overrides?.sourceTitle ?? null,
        requestId: overrides?.requestId ?? 0,
      });
    },
    [normalizedNarrativeTopic, onTopicExplainabilityStateChange],
  );

  const handleTopicExplainabilityRequest = useCallback(
    async (topic?: string) => {
      if (!isPrimaryNarrativeChart) {
        return;
      }

      const fallbackTopic = topic ?? normalizedNarrativeTopic;
      const normalizedTopic = (fallbackTopic || '').trim();
      const sourceTitle = chartExplainabilitySourceTitle;
      if (!normalizedTopic) {
        emitTopicExplainabilityChange('idle', {
          topic: '',
          loading: false,
          data: null,
          error: null,
          destinationTitle: null,
          sourceTitle,
          requestId: topicExplainabilityRequestId.current,
        });
        return;
      }

      topicExplainabilityRequestId.current += 1;
      const requestId = topicExplainabilityRequestId.current;

      if (hasExplainabilityDestination && resolvedExplainabilityDestinationTitle) {
        emitTopicExplainabilityChange('loading', {
          topic: normalizedTopic,
          loading: true,
          data: null,
          error: null,
          destinationTitle: resolvedExplainabilityDestinationTitle,
          sourceTitle,
          requestId,
        });
        return;
      }

      emitTopicExplainabilityChange('loading', {
        topic: normalizedTopic,
        loading: true,
        data: null,
        error: null,
        destinationTitle: null,
        sourceTitle,
        requestId,
      });
      try {
        const response = await fetchTopicExplainability(normalizedTopic);
        const parsed = transformExplainabilityResponse(response);
        if (topicExplainabilityRequestId.current === requestId) {
          emitTopicExplainabilityChange('success', {
            topic: normalizedTopic,
            loading: false,
            data: parsed,
            error: null,
            destinationTitle: null,
            sourceTitle,
            requestId,
          });
        }
      } catch (err) {
        if (topicExplainabilityRequestId.current === requestId) {
          const message = err instanceof Error ? err.message : 'Failed to load topic explainability';
          emitTopicExplainabilityChange('error', {
            topic: normalizedTopic,
            loading: false,
            data: null,
            error: message,
            destinationTitle: null,
            sourceTitle,
            requestId,
          });
        }
      }
    },
    [
      emitTopicExplainabilityChange,
      isPrimaryNarrativeChart,
      normalizedNarrativeTopic,
      hasExplainabilityDestination,
      resolvedExplainabilityDestinationTitle,
      chartExplainabilitySourceTitle,
    ],
  );

  useEffect(() => {
    if (!allowSma) {
      setSmaValue(1);
      return;
    }

    setSmaValue(prev => {
      const numericPrev = Number(prev);
      if (!smaOptions.includes(numericPrev)) {
        return resolvedDefaultSma;
      }
      if (smaConfig && numericPrev !== resolvedDefaultSma) {
        return resolvedDefaultSma;
      }
      if (numericPrev === 1 && resolvedDefaultSma !== 1) {
        return resolvedDefaultSma;
      }
      return numericPrev;
    });
  }, [allowSma, smaOptions, resolvedDefaultSma, smaConfig]);

  useEffect(() => {
    if (!isPrimaryNarrativeChart) return;
    const resetRequestId = ++topicExplainabilityRequestId.current;
    emitTopicExplainabilityChange('idle', {
      topic: '',
      loading: false,
      data: null,
      error: null,
      destinationTitle: null,
      sourceTitle: chartExplainabilitySourceTitle,
      requestId: resetRequestId,
    });
  }, [
    emitTopicExplainabilityChange,
    filterValuesID,
    gridFilterValuesID,
    isPrimaryNarrativeChart,
    normalizedNarrativeTopic,
    chartExplainabilitySourceTitle,
  ]);

  const chartData = useMemo(() => {
    if (!data || !data.col_nodes || data.length === 0) return;
    const obj = data;

    const colorScheme =
      obj?.color_scheme ||
      colors
        .slice(-obj.col_nodes.length)
        .reverse()
        .map((color: string) => getComputedStyle(document.documentElement).getPropertyValue(color));

    let plotBands;

    if (obj?.highcharts_config?.yAxis_custom_background_colors) {
      const valuesBg = obj?.highcharts_config?.yAxis_custom_background_values;
      const colorsBg = obj?.highcharts_config?.yAxis_custom_background_colors;
      const labelsBg = obj?.highcharts_config?.yAxis_custom_background_labels;
      plotBands = colorsBg?.map((color: string, i: number) => ({
        from: valuesBg[i],
        to: valuesBg[i + 1],
        color: hex2rgba(color, plotBandOpacity),
        label: {
          text: labelsBg[i],
          vAlign: i !== 0 ? 'top' : 'bottom',
          y: i !== 0 ? -60 : +41,
          useHtml: true,
          style: {
            color: '#000000',
          },
        },
      }));
    }

    const xLabels1 = obj.row_nodes.map((r: any) => r.name);
    const yLabels1 = obj.col_nodes.map((c: any) => c.name);
    const xLabels2 = obj.row_nodes?.map((r: any) => r.name);
    const yLabels2 = obj.secondary_col_nodes?.map((c: any) => c.name);
    const seriesData1: any[] = [];
    const seriesData2: any[] = [];
    const smoothWindow = !allowSma ? 1 : Number(smaValue);

    for (let j = 0; j < yLabels1.length; ++j) {
      const colIndex = yLabels1.length - j - 1;
      const seriesDescription = obj.col_nodes[colIndex]?.description;
      const seriesObj1: ISeriesObj = {
        name: yLabels1[colIndex],
        data: [],
        showInNavigator: true,
        color: colorScheme ? colorScheme?.[colIndex] : undefined,
        ...(typeof seriesDescription === 'string' && seriesDescription.trim()
          ? { custom: { description: seriesDescription } }
          : {}),
      };

      const tempData: Array<[number, number]> = [];

      for (let i = 0; i < xLabels1.length; ++i) {
        const value: number = obj.data[i][yLabels1.length - j - 1] as unknown as number;
        tempData.push([moment.utc(xLabels1[i]).valueOf(), value]);
      }
      tempData.sort((a, b) => a[0] - b[0]);
      if (!Number.isFinite(smoothWindow) || smoothWindow <= 1) {
        seriesObj1.data = tempData;
      } else {
        const smaResult = simpleMovingAverage(tempData, smoothWindow);
        seriesObj1.data = smaResult.length > 0 ? smaResult : tempData;
      }
      seriesData1.push(seriesObj1);
    }
    for (let j = 0; j < yLabels2.length; ++j) {
      const seriesObj2: ISeriesObj = {
        name: yLabels2[yLabels2.length - j - 1],
        data: [],
        showInNavigator: true,
        color: 'green',
      };

      const tempData: Array<[number, number]> = [];

      for (let i = 0; i < xLabels2.length; ++i) {
        const value: number = obj.secondary_data[i][yLabels2.length - j - 1] as unknown as number;
        tempData.push([moment.utc(xLabels2[i]).valueOf(), value]);
      }
      tempData.sort((a, b) => a[0] - b[0]);
      if (!Number.isFinite(smoothWindow) || smoothWindow <= 1) {
        seriesObj2.data = tempData;
      } else {
        const smaResult = simpleMovingAverage(tempData, smoothWindow);
        seriesObj2.data = smaResult.length > 0 ? smaResult : tempData;
      }
      seriesData2.push(seriesObj2);
    }
    const numberOfLines = obj?.number_of_lines;

    return { seriesData1, seriesData2, colorScheme, plotBands, numberOfLines };
  }, [data, smaValue, allowSma]);

  useEffect(() => {
    setData(allData);
    if (!allData || !allData.data || !allData.row_nodes) return;
    setData(allData);
  }, [allData]);

  const data0 = useMemo(() => {
    return chartData?.seriesData1?.[0]?.data.sort((a: any, b: any) => (a[0] > b[0] ? 1 : -1));
  }, [chartData, smaValue]);

  const data1 = useMemo(() => {
    return chartData?.seriesData2?.[0]?.data.sort((a: any, b: any) => (a[0] > b[0] ? 1 : -1));
  }, [chartData, smaValue]);

  const [chartSmaData, setChartSmaData] = useState<{ [key: string]: any }>({});

  useEffect(() => {
    const smaData = { ...chartData };
    if (smaData && smaData.seriesData1?.[0]?.data && smaData.seriesData2?.[0]?.data) {
      smaData.seriesData1[0].data = data0;
      smaData.seriesData2[0].data = data1;
    }
    setChartSmaData(smaData);
  }, [data0, data1]);

  const { size, ref: chartSizeRef }: any = useDebouncedResizeObserver(100);
  const isContainerReady = useContainerReady(size, { minWidth: 60, minHeight: 0 });

  const containerWidth = size?.width;
  const isNarrowChart = containerWidth && containerWidth < 600;

  const { indicatorSections, onClick } = useHeatmapClick(
    navHirarchyItem,
    scrollToBottom,
    gridFilterValues || filterValues,
  );

  const handleOnGridClick = useCallback(
    (props: any) => {
      onGridClick({
        ...props,
        navHirarchyItem,
        filterValues: gridFilterValues || filterValues,
        filterValuesID: gridFilterValuesID || filterValuesID,
        category,
        subcategory,
        subsubcategory,
        prism,
        prismId,
        indicatorSections,
      });
    },
    [
      navHirarchyItem,
      filterValues,
      gridFilterValues,
      category,
      subcategory,
      subsubcategory,
      prism,
      prismId,
      indicatorSections,
      onGridClick,
    ],
  );

  const onDownload = () =>
    downloadHeatmapData(
      category,
      subcategory,
      subsubcategory,
      prism,
      gridFilterValuesID || filterValuesID,
      prismId,
      resolvedChartTitle,
    ).catch(e => {
      alert(e.message);
    });

  const defaultRangeIndex = useMemo(() => getRangeSelectorIndexFromWindow(defaultTimeWindow), [defaultTimeWindow]);

  const [selectedRangeSelector, setSelectedRangeSelector] = useState<number>(
    () => defaultRangeIndex ?? DEFAULT_RANGE_SELECTOR_INDEX,
  );

  useEffect(() => {
    const nextIndex = defaultRangeIndex ?? DEFAULT_RANGE_SELECTOR_INDEX;
    setSelectedRangeSelector(prev => (prev === nextIndex ? prev : nextIndex));
  }, [defaultRangeIndex, allData]);

  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [showMean, setShowMean] = useState(false);
  const [selectedSeriesNames, setSelectedSeriesNames] = useState<string[] | null>(null);
  const [seriesOptions, setSeriesOptions] = useState<
    Array<{ name: string; color?: string; visible: boolean; description?: string }>
  >([]);
  const [meanToggleStatus, setMeanToggleStatus] = useState<{ canShow: boolean; disabled: boolean } | null>(null);
  const [isChartReady, setIsChartReady] = useState(false);
  const [chartInstanceKey, setChartInstanceKey] = useState(0);
  const previousTitleRef = useRef<string | null>(null);
  const chartReadyOnceRef = React.useRef(false);

  const yAxisDerivedLimits = useMemo(() => {
    const paddingRatio =
      typeof yAxisPaddingRatio === 'number' && yAxisPaddingRatio >= 0 && yAxisPaddingRatio < 1
        ? yAxisPaddingRatio
        : 0.05;

    const collectSeries = () => {
      const seriesCollections: any[] = [];
      if (Array.isArray(chartSmaData?.seriesData1)) {
        seriesCollections.push(...chartSmaData.seriesData1);
      }
      if (Array.isArray(chartSmaData?.seriesData2)) {
        seriesCollections.push(...chartSmaData.seriesData2);
      }
      return seriesCollections;
    };

    const allSeries = collectSeries();
    if (!allSeries.length) {
      return null;
    }

    const points: Array<[number, number]> = [];
    allSeries.forEach(series => {
      const raw = Array.isArray(series?.data) ? (series.data as Array<[number, number | null | undefined]>) : [];
      raw.forEach(entry => {
        const x = Array.isArray(entry) ? Number(entry[0]) : NaN;
        const y = Array.isArray(entry) ? Number(entry[1]) : NaN;
        if (Number.isFinite(x) && Number.isFinite(y)) {
          points.push([x, y as number]);
        }
      });
    });

    if (!points.length) {
      return null;
    }

    const xValues = points.map(([x]) => x);
    const effectiveIndex = Number.isFinite(selectedRangeSelector)
      ? Number(selectedRangeSelector)
      : DEFAULT_RANGE_SELECTOR_INDEX;
    const windowName = getRangeSelectorWindowByIndex(effectiveIndex);
    const range = computeWindowRange(windowName, xValues);

    const filteredPoints = range ? points.filter(([x]) => x >= range.min && x <= range.max) : points;
    const yValues = filteredPoints.length ? filteredPoints.map(([, y]) => y) : points.map(([, y]) => y);

    const finiteValues = yValues.filter(value => Number.isFinite(value));
    if (!finiteValues.length) {
      return null;
    }

    const limits = deriveAxisLimits(finiteValues, paddingRatio);
    if (typeof limits.min === 'number' && typeof limits.max === 'number') {
      return limits;
    }
    return null;
  }, [chartSmaData, selectedRangeSelector]);

  const initialMeanToggleStatus = useMemo(() => {
    if (!data || isLoading) {
      return { canShow: false, disabled: true };
    }

    const baseSeries = Array.isArray(chartSmaData?.seriesData1)
      ? chartSmaData.seriesData1.filter((series: any) => series && series.name && !/^Navigator \d+$/.test(series.name))
      : [];

    const canShow = baseSeries.length > 1;
    return { canShow, disabled: baseSeries.length < 2 };
  }, [chartSmaData, data, isLoading]);

  const resolvedMeanToggleStatus = meanToggleStatus ?? initialMeanToggleStatus;
  const shouldRenderMeanToggle = allowMean && resolvedMeanToggleStatus.canShow;

  const seriesOptionNames = useMemo(() => seriesOptions.map(option => option.name), [seriesOptions]);

  const showSeriesSelector = allowDropdownLegend && seriesOptions.length > 1;
  const isSeriesSelectDisabled = !allowDropdownLegend || !seriesOptions.length;

  const handleMeanToggleChange = useCallback(
    (checked: boolean) => {
      if (resolvedMeanToggleStatus.disabled && checked) {
        return;
      }
      setShowMean(checked);
    },
    [resolvedMeanToggleStatus.disabled],
  );

  const handleSeriesMetadataChange = useCallback(
    (meta: Array<{ name: string; color?: string; visible: boolean; description?: string }>) => {
      setSeriesOptions(meta);
    },
    [],
  );

  const handleSeriesSelectionChange = useCallback(
    (selection: string[]) => {
      const selectionSet = new Set(selection);
      const orderedSelection = seriesOptionNames.filter(name => selectionSet.has(name));
      if (orderedSelection.length === seriesOptionNames.length) {
        setSelectedSeriesNames(null);
        return;
      }
      setSelectedSeriesNames(orderedSelection);
    },
    [seriesOptionNames],
  );

  const isReady = isContainerReady && !isLoading && (data ? isChartReady : true);
  const canRenderChart = isContainerReady && !isLoading && Boolean(data);
  const shouldRenderControls = Boolean(
    data && isReady && !isExportMenuOpen && (allowSma || showSeriesSelector || shouldRenderMeanToggle),
  );

  useEffect(() => {
    if (!data) {
      setIsExportMenuOpen(false);
    }
  }, [data]);

  useEffect(() => {
    if (resolvedMeanToggleStatus.disabled && showMean) {
      setShowMean(false);
    }
  }, [resolvedMeanToggleStatus.disabled, showMean]);

  useEffect(() => {
    if (!allowMean && showMean) {
      setShowMean(false);
    }
  }, [allowMean, showMean]);

  useEffect(() => {
    const normalizedTitle = resolvedChartTitle || '';
    if (previousTitleRef.current === null) {
      previousTitleRef.current = normalizedTitle;
      return;
    }
    if (previousTitleRef.current !== normalizedTitle) {
      previousTitleRef.current = normalizedTitle;
      setChartInstanceKey(prev => prev + 1);
    }
  }, [resolvedChartTitle]);

  useEffect(() => {
    if (isLoading) {
      setMeanToggleStatus(null);
      setShowMean(false);
      setSelectedSeriesNames(null);
      setSeriesOptions([]);
      setIsChartReady(false);
      chartReadyOnceRef.current = false;
      setChartInstanceKey(prev => prev + 1);
    }
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading && !data) {
      setIsChartReady(true);
    }
  }, [data, isLoading]);

  useEffect(() => {
    if (!isContainerReady) {
      setIsChartReady(false);
      chartReadyOnceRef.current = false;
    }
  }, [isContainerReady]);

  const handleChartReady = useCallback(() => {
    if (chartReadyOnceRef.current) return;
    chartReadyOnceRef.current = true;
    setIsChartReady(true);
  }, []);

  const displayedSmaValue = React.useMemo(() => {
    if (!smaOptions.includes(smaValue)) {
      return resolvedDefaultSma;
    }
    return smaValue;
  }, [smaOptions, smaValue, resolvedDefaultSma]);

  return (
    <ScrollWrap ref={scrollerRef}>
      {error ? <AlertBar message={(error as unknown as any).message} /> : <div />}
      <MainContainer>
        {!isReady && (
          <LoadingOverlay>
            <OverlayLoader />
          </LoadingOverlay>
        )}
        <ContentFade $visible={isReady ? 1 : 0}>
          <ContentContainer>
            <HeatmapContainer ref={chartSizeRef}>
              <RelativeContainer>
                {canRenderChart && (
                  <YAxisLineChart
                    key={chartInstanceKey}
                    {...{
                      data0: chartSmaData?.seriesData1?.[0]?.data.sort((a: any, b: any) => (a[0] > b[0] ? 1 : -1)),
                      data: chartSmaData?.seriesData1,
                      data1: chartSmaData?.seriesData2?.[0]?.data.sort((a: any, b: any) => (a[0] > b[0] ? 1 : -1)),
                      name0: chartSmaData?.seriesData1?.[0]?.name,
                      name1: chartSmaData?.seriesData2?.[0]?.name,
                      size,
                      onClick: gridIndex || gridIndex === 0 ? handleOnGridClick : onClick,
                      prism,
                      filterValues: gridFilterValues || filterValues,
                      colorScheme: chartSmaData?.colorScheme,
                      chartTitle: resolvedChartTitle,
                      chartSubtitle,
                      chartYtitle,
                      tooltipCfg,
                      gridHeight,
                      gridIndex,
                      gridPrism,
                      plotBands: chartSmaData?.plotBands,
                      numberOfLines: chartSmaData?.numberOfLines,
                      smaValue,
                      selectedRangeSelector,
                      setSelectedRangeSelector,
                      showMean,
                      onShowMeanChange: setShowMean,
                      onMeanToggleStatusChange: setMeanToggleStatus,
                      onChartReady: handleChartReady,
                      onTooltipVisibilityChange: setChartTooltipVisible,
                      selectedSeriesNames,
                      onSelectedSeriesNamesChange: setSelectedSeriesNames,
                      onSeriesMetadataChange: handleSeriesMetadataChange,
                      smaEnabled: allowSma,
                      meanEnabled: allowMean,
                      dropdownLegendEnabled: allowDropdownLegend,
                      onExportMenuVisibilityChange: setIsExportMenuOpen,
                      onTopicExplainabilityRequest: handleTopicExplainabilityRequest,
                      topicForExplainability: normalizedNarrativeTopic,
                      yAxisRangeMode,
                      yAxisPaddingRatio,
                      yAxisMin: yAxisDerivedLimits?.min,
                      yAxisMax: yAxisDerivedLimits?.max,
                    }}
                  />
                )}
              </RelativeContainer>
            </HeatmapContainer>
            {!gridIndex && gridIndex !== 0 && (
              <FilterBar
                type={navHirarchyItem?.type}
                filters={navHirarchyItem?.filters}
                values={gridFilterValues || filterValues}
                filterHierarchy={navHirarchyItem?.narrativeFilterTree}
                filterHierarchyValues={gridFilterValuesID || filterValuesID}
                onDownload={onDownload}
                tooltipCfg={tooltipCfg}
              />
            )}
          </ContentContainer>
          {shouldRenderControls && (
            <ControlsWrapper>
              <SeriesControls
                compact={isNarrowChart}
                showMeanToggle={shouldRenderMeanToggle}
                meanChecked={showMean}
                meanDisabled={resolvedMeanToggleStatus.disabled}
                onMeanChange={handleMeanToggleChange}
                topRowExtra={
                  allowSma && !chartTooltipVisible ? (
                    <>
                      <SelectTitle className="select-title">SMA:</SelectTitle>
                      <Select
                        options={smaOptions}
                        value={displayedSmaValue}
                        onChange={handleSMAChange}
                        controlHeight={34}
                        selectFontSize={15}
                        selectFontWeight={400}
                        selectColor="#33485e"
                      />
                    </>
                  ) : undefined
                }
                seriesOptions={showSeriesSelector ? seriesOptions : []}
                selectedSeries={selectedSeriesNames}
                onSeriesChange={handleSeriesSelectionChange}
                seriesDisabled={isSeriesSelectDisabled || !isReady}
                seriesLabel="Series:"
                menuWidth={isNarrowChart ? 180 : 220}
                seriesTooltipCfg={tooltipCfg}
              />
            </ControlsWrapper>
          )}
        </ContentFade>
      </MainContainer>
      <IndicatorsContainer>
        {!gridIndex &&
          gridIndex !== 0 &&
          indicatorSections.map((section: any, index: number) => (
            <IndicatorContainer
              key={'container' + section.indicator + section.topic + section.symbol + String(index)}
              $iswide={indicatorSections.length === index + 1 && indicatorSections.length % 2 !== 0 ? 1 : 0}
            >
              {section.type === 'bar' && (
                <IndicatorStackedAreaChart
                  key={section.indicator + section.topic + section.symbol + String(index)}
                  {...{
                    category,
                    prism,
                    prismId,
                    ...section,
                    filterValues: gridFilterValues || filterValues,
                    tooltipCfg,
                  }}
                ></IndicatorStackedAreaChart>
              )}
              {section.type === 'line' && (
                <IndicatorLineChart
                  key={section.indicator + section.topic + section.symbol + String(index)}
                  {...{
                    category,
                    prism,
                    prismId,
                    ...section,
                    filterValues: gridFilterValues || filterValues,
                    tooltipCfg,
                  }}
                ></IndicatorLineChart>
              )}
              {section.type === 'table' && (
                <IndicatorTable
                  key={section.indicator + section.topic + section.symbol + String(index)}
                  {...{
                    category,
                    prism,
                    prismId,
                    ...section,
                    filterValues: gridFilterValues || filterValues,
                    tooltipCfg,
                  }}
                ></IndicatorTable>
              )}
            </IndicatorContainer>
          ))}
      </IndicatorsContainer>
      <FabContainer>
        {indicatorSections.length > 0 && (
          <Fab onClick={scrollToTop}>
            <ExpandLess />
          </Fab>
        )}
      </FabContainer>
    </ScrollWrap>
  );
}
